import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage, t } from '../utils/i18n';
import { formatCurrency } from '../services/stripeService';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4242';

const OrderConfirmationPage = () => {
  const location = useLocation();
  const { language } = useLanguage();
  const [status, setStatus] = useState('loading'); // loading, success, pending, error
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState('');

  const verifyOrder = useCallback(async (sessionId) => {
    if (!sessionId) {
      setStatus('error');
      setError('No se encontró un ID de sesión para verificar.');
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/get-order-status?sessionId=${sessionId}`);
      if (!response.ok) {
        throw new Error('No se pudo comunicar con el servidor para verificar el pedido.');
      }
      
      const data = await response.json();
      setOrderDetails(data);

      if (data.paymentStatus === 'paid') {
        setStatus('success');
      } else if (data.paymentStatus === 'unpaid') {
        setStatus('pending');
      } else {
        setStatus('error');
        setError(`El estado del pago es: ${data.paymentStatus}`);
      }
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Ocurrió un error al verificar tu pedido.');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get('session_id');
    verifyOrder(sessionId);
  }, [location, verifyOrder]);

  if (status === 'loading') {
    return (
      <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-b from-[#F2EBE6] to-[#D5BCA3]">
        <div className="max-w-2xl mx-auto text-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-akasha-brown mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-akasha-brown">
            Verificando tu pedido...
          </h2>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="relative flex items-center justify-center min-h-screen bg-gradient-to-b from-red-100 to-red-200">
        <div className="max-w-2xl mx-auto text-center p-8 bg-white/30 backdrop-blur-md rounded-[2rem] border border-red-300">
           <div className="bg-red-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          <h1 className="text-3xl font-bold text-red-800 mb-4">Error en la Orden</h1>
          <p className="text-red-700 mb-6">{error}</p>
          <Link
            to="/store"
            className="inline-block bg-akasha-brown text-white px-8 py-3 rounded-lg hover:bg-akasha-brown/90 transition-colors duration-300 font-semibold"
          >
            Volver a la tienda
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#F2EBE6] to-[#D5BCA3] py-16">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto">
          {status === 'success' && (
             <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-8">
                <div className="bg-green-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                   <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                   </svg>
                </div>
                <h1 className="text-4xl font-bold text-akasha-brown mb-4">¡Gracias por tu compra!</h1>
                <p className="text-xl text-akasha-black/80">Tu pedido ha sido procesado exitosamente.</p>
             </motion.div>
          )}

          {status === 'pending' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-8">
               <div className="bg-amber-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
               </div>
               <h1 className="text-4xl font-bold text-akasha-brown mb-4">Pago Pendiente</h1>
               <p className="text-xl text-akasha-black/80">Estamos esperando la confirmación de tu pago.</p>
               <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800">
                  Recibirás una confirmación por correo electrónico una vez que el pago se haya procesado (puede tardar hasta 24 horas para pagos en OXXO).
               </div>
            </motion.div>
         )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-md rounded-[2rem] p-8 border border-white/20 mb-8"
          >
            <h2 className="text-2xl font-semibold text-akasha-brown mb-6">Detalles del Pedido</h2>
            {orderDetails && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="font-semibold text-akasha-brown mb-2">Número de Pedido</h3>
                        <p className="text-akasha-black font-mono text-lg">{orderDetails.orderId}</p>
                    </div>
                    <div>
                        <h3 className="font-semibold text-akasha-brown mb-2">Total Pagado</h3>
                        <p className="text-akasha-black font-mono text-lg">{formatCurrency(orderDetails.total)}</p>
                    </div>
                    <div>
                        <h3 className="font-semibold text-akasha-brown mb-2">Estado del Pago</h3>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            status === 'success' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                            {status === 'success' ? '✓ Pagado' : 'Pendiente'}
                        </span>
                    </div>
                     <div>
                        <h3 className="font-semibold text-akasha-brown mb-2">Email</h3>
                        <p className="text-akasha-black">{orderDetails.customerEmail}</p>
                    </div>
                </div>
                <h3 className="font-semibold text-akasha-brown mt-6 mb-4">Artículos</h3>
                <ul className="space-y-3">
                  {orderDetails.items.map((item, index) => (
                    <li key={index} className="flex justify-between items-center text-akasha-black">
                      <span>{item.name} x {item.quantity}</span>
                      <span>{formatCurrency(item.price * item.quantity)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center"
          >
            <Link
              to="/catalog"
              className="inline-block bg-akasha-brown text-white px-8 py-3 rounded-lg hover:bg-akasha-brown/90 transition-colors duration-300 font-semibold"
            >
              Continuar Comprando
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage; 